import { isEmpty, isEqual } from "lodash";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, DropResult, Droppable } from "react-beautiful-dnd";
import { FieldError } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { useBandContext, usePermissionsContext } from "Bands/contexts";
import { EditRow } from "Bands/pages/band-settings/components";
import { RedirectToCustomEventBlockPermissionsModal } from "Bands/pages/members/RedirectToCustomEventBlockPermissionsModal";
import {
  clearActionMeta,
  dataMetaSelector,
  editBandSettingsActionCreator,
  getPermissionTypes,
} from "Bands/state";
import { BandSettingsDataKeys, IEditItem, IIconBandSetting } from "Bands/types";
import { getBandSettingActionFromKey, useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Plus } from "shared/assets/icons";
import { Button, Loader, Message, Required } from "shared/components";
import { Spacer } from "shared/layout";
import { Icon } from "shared/styles";
import { slideInFromTop } from "shared/styles/constants";
import { IAPIState, IDataKeys, IStatus, ITheme } from "shared/types";
import { ICONS } from "shared/utils";
import { DeleteEditRowModal } from "./DeleteEditRowModal";
import { StyledHeaderItem, StyledRow, StyledTable } from "./styles";

const tableCopy = copy.bandSettings.eventSettings.table;
const formCopy = copy.form;

const reorder = (list: IEditItem[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

interface EditTableProps {
  name: string;
  initialItems: IEditItem[];
  hasIcon?: boolean;
  hasDragAndDrop?: boolean;
  addCopy: string;
  dataKey: BandSettingsDataKeys;
  modalTitle: string;
  modalMessage: string;
}

export const EditTable: FC<EditTableProps> = ({
  name,
  addCopy,
  hasDragAndDrop,
  hasIcon,
  initialItems,
  dataKey,
  modalTitle,
  modalMessage,
}) => {
  const dispatch = useDispatch();
  const { bandId, isLoading } = useBandContext();
  const { rootGroups } = usePermissionsContext();
  const [items, setItems] = useState<IEditItem[]>([]);
  const [errors, setErrors] = useState<Record<string, FieldError | null>>({});
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<string | null>(null);
  const [newItems, setNewItems] = useState<IEditItem[]>([]);

  const action = useMemo(() => getBandSettingActionFromKey(dataKey), [dataKey]);

  const { api: apiMeta } = useSelector(dataMetaSelector(action));

  useClearActionMetaOnUnMount(action);

  const initialItemRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !initialItemRef.current && initialItems) {
      setItems(initialItems);
      initialItemRef.current = true;
    }
  }, [isLoading, initialItems]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(action));
      dispatch(getPermissionTypes(bandId!));

      setItems(initialItems);
    }
  }, [apiMeta?.status, initialItems, action, dispatch, bandId, rootGroups, items]);

  const canDeleteItem = useMemo(() => {
    if (dataKey === IDataKeys.eventInfoBlockTypes) {
      return true;
    }
    return items.length > 1;
  }, [items, dataKey]);

  const handleDeleteItem = useCallback(
    (deleteId: string) => {
      setItems(items.filter(({ id }) => id !== deleteId));
    },
    [setItems, items]
  );

  const saveRequired = useMemo(() => {
    return !isEqual(initialItems, items);
  }, [initialItems, items]);

  const addItem = () => {
    const newItem: IEditItem = {
      id: `new-${items.length}`,
      label: "",
    };

    if (hasIcon) {
      (newItem as IIconBandSetting).icon = ICONS.allBands;
    }

    setItems([...items, newItem]);
  };

  const updateItem = (updatedItem: IEditItem) => {
    const newItems = items.map(item => (item.id === updatedItem.id ? updatedItem : item));

    setItems(newItems);

    if (!isEmpty(errors)) {
      validateFields(newItems);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || !items) {
      return;
    }

    const newList = reorder(items, result.source.index, result.destination.index);

    setItems(newList);
  };

  const validateFields = useCallback((items: IEditItem[]) => {
    let hasErrors = false;
    items.forEach(({ id, label }) => {
      if (!label.trim()?.length) {
        hasErrors = true;
        setErrors(errors => ({
          ...errors,
          [id]: { type: "required" },
        }));
      } else {
        setErrors(errors => ({
          ...errors,
          [id]: null,
        }));
      }
    });
    return hasErrors;
  }, []);

  const handleSave = () => {
    if (!isEqual(initialItems, items)) {
      // check for errors
      const hasErrors = validateFields(items);

      if (!hasErrors) {
        const newItems = [...items.filter(({ id }) => id.startsWith("new"))];
        setNewItems(newItems);

        if (newItems.length) {
          setModalIndex(0);
        }

        editBandSettingsActionCreator({
          oldItems: initialItems,
          newItems: items,
          dataKey,
          bandId: bandId as string,
          dispatch,
        });
      }
    }
  };

  const headers = useMemo(() => {
    const headers: JSX.Element[] = [];

    if (hasDragAndDrop) {
      headers.push(<StyledHeaderItem key={1} />);
    }
    if (hasIcon) {
      headers.push(
        <StyledHeaderItem key={2}>
          {tableCopy.icon}
          <Required />
        </StyledHeaderItem>
      );
    }

    headers.push(
      <StyledHeaderItem key={3}>
        {tableCopy.label}
        <Required />
      </StyledHeaderItem>
    );
    headers.push(<StyledHeaderItem key={4} />);

    return headers;
  }, [hasDragAndDrop, hasIcon]);

  const newRootGroups = useMemo(() => {
    return rootGroups?.filter(({ label }) =>
      newItems.some(({ label: newItemLabel }) => {
        return label === `Event: ${newItemLabel}`;
      })
    );
  }, [rootGroups, newItems]);

  return (
    <Spacer size="m">
      {showModal && (
        <DeleteEditRowModal
          id={showModal}
          title={modalTitle}
          message={modalMessage}
          handleClose={() => setShowModal(null)}
          handleConfirm={id => {
            setShowModal(null);
            handleDeleteItem(id);
          }}
        />
      )}
      {dataKey === IDataKeys.eventInfoBlockTypes &&
        !!newRootGroups?.length &&
        modalIndex !== null &&
        modalIndex < newRootGroups.length &&
        newRootGroups?.[modalIndex]?.id && (
          <RedirectToCustomEventBlockPermissionsModal
            key={newRootGroups[modalIndex].id}
            groupId={newRootGroups[modalIndex].id}
            handleClose={() => setModalIndex(prev => (prev as number) + 1)}
          />
        )}
      {!!items.length && (
        <StyledTable>
          <StyledRow hasDragAndDrop={hasDragAndDrop} hasIcon={hasIcon}>
            {headers.map(header => header)}
          </StyledRow>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={name}>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) => (
                    <EditRow
                      key={index}
                      index={index}
                      item={item}
                      handleDelete={() => setShowModal(item.id)}
                      hasDragAndDrop={hasDragAndDrop}
                      hasIcon={hasIcon}
                      updateItem={updateItem}
                      canDeleteItem={canDeleteItem}
                      error={errors[item.id]}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </StyledTable>
      )}
      <Button theme={ITheme.secondary} onClick={addItem} data-testid={`${dataKey}_add_button`}>
        <Icon as={Plus} />
        <span>{addCopy}</span>
      </Button>
      {saveRequired && (
        <Message {...slideInFromTop} status={IStatus.warning}>
          {formCopy.saveChanges}
        </Message>
      )}
      <Button
        onClick={handleSave}
        data-testid={`${dataKey}_save_button`}
        disabled={!saveRequired || apiMeta?.status === IAPIState.pending}
      >
        <Spacer size="xxs" spaceDirection="horizontal">
          <div>{formCopy.save}</div>
          {apiMeta?.status === IAPIState.pending && <Loader size="s" />}
        </Spacer>
      </Button>
      {apiMeta?.error && <Message status={IStatus.error}>{copy.errors.general}</Message>}
    </Spacer>
  );
};
